import { api, tagTypes } from "./api";

type PaginationParams = {
  page?: number;
  limit?: number;
  vendor_type?:"individual";
  search?: string;
  status?: string;
  order?: string;
  lastLoginRange?: string;
};

// Inject artisan endpoints into the main API
export const artisanApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // GET: All artisans

    getAllArtisans: builder.query<any, PaginationParams>({
      query: ({ page = 1, limit = 10, vendor_type="", search='', status='', order='desc', lastLoginRange=''}) => ({
        url: "artisan/v1/",
        params: { page, limit, vendor_type, search, status, order, lastLoginRange},
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // GET: Stale artisans list
    getAllStaleArtisans: builder.query<any, PaginationParams>({
      query: ({ page = 1, limit = 10, lastLoginRange='' }) => ({
        url: "artisan/v1/dashboard/artisan/stale",
        params: { page, limit, lastLoginRange },
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // DELETE: Remove stale artisans
    deleteStaleArtisans: builder.mutation<any, { artisanIds: string[] }>({
      query: (payload) => ({
        url: `artisan/v1/dashboard/artisan/stale`,
        method: "DELETE",
        body: payload,
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),
    // DELETE: Remove stale artisans
    deleteFeedback: builder.mutation<any, { id: string, type: string }>({
      query: ({id, type}) => ({
        url: `/${type}/v1/dashboard/${type}/feedback/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [tagTypes.Vendor, tagTypes.User],
    }),

    // GET: All artisan verifications
    getAllArtisansVerification: builder.query<any, void>({
      query: () => ({ url: "artisan/v1/verification", method: "GET" }),
      providesTags: [tagTypes.Vendor],
    }),

    // GET: Single artisan profile
    getArtisan: builder.query<any, string>({
      query: (artisanId) => ({
        url: `artisan/v1/profile/${artisanId}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // GET: Single artisan verification
    getArtisanVerification: builder.query<any, string>({
      query: (artisanId) => ({
        url: `artisan/v1/verification/${artisanId}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // POST: Suspend artisan
    suspendArtisan: builder.mutation<any, { artisanId: string; data: any }>({
      query: ({ artisanId, data }) => ({
        url: `artisan/v1/${artisanId}/suspend`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // POST: Unsuspend artisan
    unSuspendArtisan: builder.mutation<any, { artisanId: string; data: any }>({
      query: ({ artisanId, data }) => ({
        url: `artisan/v1/${artisanId}/unsuspend`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // PUT: Update artisan profile
    updateArtisan: builder.mutation<any, { artisanId: string; data: any }>({
      query: ({ artisanId, data }) => ({
        url: `artisan/v1/profile/${artisanId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // POST: Validate account details
    validateAccount: builder.mutation<
      any,
      { account_number: string; bank_code: string }
    >({
      query: (payload) => ({
        url: "artisan/v1/admin/wallet/verify-account",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [tagTypes.Wallet],
    }),

    // POST: Update withdrawal bank
    updateWithdrawalBank: builder.mutation<
      any,
      {
        account_number: string;
        bank_code: string;
        bank_name: string;
        user_id?: string;
        artisan_id?: string;
      }
    >({
      query: (payload) => ({
        url: "artisan/v1/admin/wallet/bank",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [tagTypes.Wallet],
    }),

    // GET: Dump artisans data
    DumpArtisansData: builder.query<any, void>({
      query: () => ({ url: "artisan/v1/dashboard/dump-data?vendor_type=individual", method: "GET" }),
      providesTags: [tagTypes.Vendor],
    }),

    // GET: Dump artisans data
    DumpPartnersData: builder.query<any, void>({
      query: () => ({ url: "artisan/v1/dashboard/dump-data?vendor_type=partner", method: "GET" }),
      providesTags: [tagTypes.Vendor],
    }),

    // POST: Charge verification fee
    chargeVerificationFee: builder.mutation<any, string>({
      query: (artisanId) => ({
        url: `artisan/v1/verification/${artisanId}/charge-fee`,
        method: "POST",
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // PUT: Delete profile picture
    deleteProfilePic: builder.mutation<any, string>({
      query: (artisanId) => ({
        url: `artisan/v1/profile/remove_profile_picture/${artisanId}`,
        method: "PUT",
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // PUT: Bring vendors online/offline
    bringVendorsOnline: builder.mutation<any, { status: string }>({
      query: ({ status }) => ({
        url: `artisan/v1/profile/all/availability`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // GET: Artisan feedbacks
    getArtisanFeedbacks: builder.query<
      any,
      { page?: number; emojiRating?: string; limit?: number; search?: string }
    >({
      query: ({ page = 1, emojiRating = "", limit = 10, search = ''  } = {}) => ({
        url: `artisan/v1/dashboard/artisan/feedback?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // GET: Pinned artisan feedbacks
    getArtisanPinnedFeedbacks: builder.query<
      any,
      { page?: number; emojiRating?: string; limit?: number; search?: string }
    >({
      query: ({ page = 1, emojiRating = "", limit = 10, search = '' } = {}) => ({
        url: `artisan/v1/dashboard/artisan/pinned-feedback?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
        method: "GET",
      }),
      providesTags: [tagTypes.Vendor],
    }),

    // PUT: Pin/unpin artisan feedback
    pinArtisanFeedback: builder.mutation<any, { id: string; pinned: boolean; page?: number; emojiRating?: string; limit?: number; search?: string }>({
      query: ({ id, pinned, page = 1, emojiRating = "", limit = 10, search = '' }) => ({
        url: `artisan/v1/dashboard/artisan/pin-feedback/${id}?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
        method: "PUT",
        body: { pinned },
      }),
      invalidatesTags: [tagTypes.Vendor],
    }),

    // GET: Export artisan feedback (data dump)
    exportArtisanFeedback: builder.query<any, void>({
      query: () => ({ url: "artisan/v1/dashboard/artisan", method: "GET" }),
      providesTags: [tagTypes.Vendor],
    }),
  }),
});

export const {
  useGetAllArtisansQuery,
  useGetAllStaleArtisansQuery,
  useDeleteStaleArtisansMutation,
  useDeleteFeedbackMutation,
  useGetAllArtisansVerificationQuery,
  useGetArtisanQuery,
  useGetArtisanVerificationQuery,
  useSuspendArtisanMutation,
  useUnSuspendArtisanMutation,
  useUpdateArtisanMutation,
  useValidateAccountMutation,
  useUpdateWithdrawalBankMutation,
  useDumpArtisansDataQuery,
  useLazyDumpArtisansDataQuery,
  useDumpPartnersDataQuery,
  useLazyDumpPartnersDataQuery,
  useChargeVerificationFeeMutation,
  useDeleteProfilePicMutation,
  useBringVendorsOnlineMutation,
  useGetArtisanFeedbacksQuery,
  useLazyGetArtisanFeedbacksQuery,
  useGetArtisanPinnedFeedbacksQuery,
  useLazyGetArtisanPinnedFeedbacksQuery,
  usePinArtisanFeedbackMutation,
  useExportArtisanFeedbackQuery,
  useLazyExportArtisanFeedbackQuery,
} = artisanApi;
